/**
 * «Repaso del día» en el Inicio (charla asistida, encargo 99): las correcciones
 * de charlas anteriores que ya tocan repasar (`GET /corrections/due`, el primer
 * lector de `next_review_at`). Hasta este cambio la app anotaba cuándo volvía
 * cada corrección pero nada lo leía: «vuelve mañana» no volvía nunca.
 *
 * Usa el molde de la tarjeta de entrada a Órbita (`orb-entry`, de `orbitas.css`;
 * esa entrada se sacó del Inicio en la #525), arriba de los héroes. Abre el
 * mismo repaso que el cierre. Tope de 5 por día, del server. Sin
 * nada que repasar (o sin respuesta del server) no se dibuja: nunca un 0
 * inventado. Al cerrar el repaso se vuelve a pedir la lista.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchDueCorrections,
  fetchReviewCues,
  type DueCorrection,
} from '../../../lib/correctionReviewApi.js';
import {
  CoachReview,
  warmReviewOpening,
  type CoachReviewInput,
} from '../../conversation/assisted/CoachReview.js';
import '../../orbitas/orbitas.css';

export function DailyReviewCard() {
  const [items, setItems] = useState<DueCorrection[] | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback((signal?: AbortSignal) => {
    fetchDueCorrections(signal)
      .then((d) => {
        setItems(d.items);
        // Las pistas del «sin mirar» se escriben la primera vez: se piden ya,
        // para que al tocar estén listas.
        if (d.items.length > 0) {
          fetchReviewCues(d.items.map((i) => i.id)).catch((err: unknown) =>
            console.warn('[home] review cues prefetch failed', err),
          );
          // Y la voz de la primera línea del coach: al tocar, habla ya (card #595).
          warmReviewOpening(d.items.map(toReviewItem), 'day');
        }
      })
      .catch((err: unknown) => {
        if (signal?.aborted) return;
        console.warn('[home] daily review failed', err);
        setItems(null);
      });
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const reviewItems = useMemo(() => (items ?? []).map(toReviewItem), [items]);

  if (!items || items.length === 0) return null;
  const first = items[0]!;
  // Con frases de la charla asistida o palabras guardadas, no son todas correcciones (Emmanuel, 19/10).
  const allCorrections = items.every((i) => i.kind !== 'phrase' && i.kind !== 'word');
  const sub =
    items.length === 1
      ? `1 ${allCorrections ? 'corrección' : 'frase'} te toca hoy: «${first.corrected}»`
      : `${items.length} ${allCorrections ? 'correcciones' : 'frases'} te tocan hoy`;

  return (
    <>
      <button type="button" className="orb-entry daily-review-entry" onClick={() => setOpen(true)}>
        <span className="orb-entry__ring" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            width={20}
            height={20}
            fill="none"
            stroke="var(--lime)"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4.5 9 a 8 8 0 1 1 -0.6 5.5" />
            <path d="M4 4.5 V 9.5 H 9" />
          </svg>
        </span>
        <span className="orb-entry__txt">
          <span className="orb-entry__title">Repaso del día</span>
          <span className="orb-entry__sub">{sub}</span>
        </span>
        <span className="orb-entry__chev" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            width={18}
            height={18}
            fill="none"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 6 L 15 12 L 9 18" />
          </svg>
        </span>
      </button>
      {open && (
        <CoachReview
          items={reviewItems}
          source="day"
          finishLabel="Listo"
          onExit={() => {
            setOpen(false);
            load();
          }}
        />
      )}
    </>
  );
}

/** Lo que el repaso necesita de una del día. */
function toReviewItem(i: DueCorrection): CoachReviewInput {
  return {
    ...(i.kind === 'phrase' ? { kind: 'phrase' as const } : {}),
    // Una palabra guardada (Emmanuel, 20/10): la tarjeta la nombra.
    ...(i.kind === 'word' ? { kind: 'word' as const, ...(i.word ? { word: i.word } : {}) } : {}),
    ...(i.radar ? { radar: i.radar } : {}),
    id: i.id,
    memberIds: i.memberIds,
    said: i.said,
    corrected: i.corrected,
    explanation: i.explanation,
    returning: i.reviewed,
  };
}
